// Saves documents (debounced per key) so the server always ends with the
// latest version: one request in flight per key, the newest value waiting
// behind it, serialized only when it's sent. Failures that can pass (network,
// server) are retried; an expired session or a rejected document are not.
// No DOM or API imports: documents.ts wires it, tests drive it.

export interface SaverDeps {
  readonly put: (key: string, json: string, options: { readonly keepalive: boolean }) => Promise<unknown>
  readonly retryable: (error: unknown) => boolean
  /** A save that won't succeed by trying again (e.g. the document was rejected), or that succeeded after one. */
  readonly onStatus?: (key: string, status: "saved" | "failed") => void
  readonly setTimer?: (run: () => void, ms: number) => unknown
  readonly clearTimer?: (timer: unknown) => void
}

export interface DocumentSaver {
  /** The document changed; `serialize` gives its newest JSON when it's sent. */
  readonly schedule: (key: string, serialize: () => string) => void
  /** Sends everything pending now; resolves when nothing is in flight. */
  readonly flush: (options?: { readonly keepalive?: boolean }) => Promise<void>
  /** Drops everything pending (another user signed in). */
  readonly discard: () => void
}

/** Then every 60 s for as long as it takes: a value is never dropped while it can still be saved. */
const RETRY_MS = [2_000, 10_000, 30_000, 60_000]
/** Browsers cap the bodies of keepalive requests in flight at 64 KiB in total. */
const KEEPALIVE_BUDGET = 60 * 1024

interface Entry {
  serialize: (() => string) | null
  timer: unknown
  inFlight: Promise<void> | null
  attempt: number
  /** The last save was refused for good (reported once until a save succeeds). */
  failed: boolean
}

export const createDocumentSaver = (delayMs: number, deps: SaverDeps): DocumentSaver => {
  const setTimer = deps.setTimer ?? ((run, ms) => setTimeout(run, ms))
  const clearTimer = deps.clearTimer ?? ((timer) => clearTimeout(timer as ReturnType<typeof setTimeout>))
  const entries = new Map<string, Entry>()
  let generation = 0

  const entryOf = (key: string): Entry => {
    let entry = entries.get(key)
    if (entry === undefined) {
      entry = { serialize: null, timer: undefined, inFlight: null, attempt: 0, failed: false }
      entries.set(key, entry)
    }
    return entry
  }

  const send = (key: string, keepalive: boolean): void => {
    const entry = entryOf(key)
    clearTimer(entry.timer)
    entry.timer = undefined
    // One at a time per key: the newest value goes when this one is done.
    if (entry.inFlight !== null || entry.serialize === null) return
    const json = entry.serialize()
    entry.serialize = null
    const sentIn = generation
    entry.inFlight = deps.put(key, json, { keepalive }).then(
      () => {
        if (entry.attempt > 0 || entry.failed) deps.onStatus?.(key, "saved")
        entry.attempt = 0
        entry.failed = false
      },
      (error: unknown) => {
        if (sentIn !== generation) return
        if (!deps.retryable(error)) {
          entry.failed = true
          deps.onStatus?.(key, "failed")
          return
        }
        // Still the newest unless the document changed meanwhile.
        entry.serialize ??= () => json
        entry.timer = setTimer(() => send(key, false), RETRY_MS[Math.min(entry.attempt++, RETRY_MS.length - 1)]!)
      }
    ).finally(() => {
      entry.inFlight = null
      if (sentIn !== generation) return
      if (entry.serialize !== null && entry.timer === undefined) send(key, false)
      else if (entry.serialize === null && entry.timer === undefined) entries.delete(key)
    })
  }

  return {
    schedule: (key, serialize) => {
      const entry = entryOf(key)
      entry.serialize = serialize
      entry.attempt = 0
      clearTimer(entry.timer)
      entry.timer = setTimer(() => send(key, false), delayMs)
    },
    flush: async ({ keepalive = false } = {}) => {
      let budget = KEEPALIVE_BUDGET
      for (const [key, entry] of entries) {
        if (entry.serialize === null) continue
        // Nothing waits for its debounce (or a retry) any more.
        clearTimer(entry.timer)
        entry.timer = undefined
        const json = entry.serialize()
        entry.serialize = () => json
        const fits = keepalive && json.length * 3 <= budget
        if (fits) budget -= json.length * 3
        if (entry.inFlight === null) {
          send(key, fits)
        } else if (fits) {
          // The page is going away: the follow-up of the request in flight wouldn't outlive it.
          entry.serialize = null
          void deps.put(key, json, { keepalive: true }).catch(() => undefined)
        }
        // Otherwise the request in flight sends the newest value when it's done.
      }
      // Until every follow-up is done too.
      for (;;) {
        const inFlight = [...entries.values()].flatMap((entry) => (entry.inFlight ? [entry.inFlight] : []))
        if (inFlight.length === 0) return
        await Promise.all(inFlight)
      }
    },
    discard: () => {
      generation++
      for (const entry of entries.values()) clearTimer(entry.timer)
      entries.clear()
    }
  }
}
